import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import Button from 'tailwind-components/Button/Button';
import { ONEILL_MAIN_AGENCY_ID, USER_TYPES } from 'constants/enum';
import icons from 'constants/icons';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { downloadAllBOBData } from 'state/actions/healthSherpa';
import HeaderFilterToolTip from 'views/DashboardCustomers/HeaderFilter/HeaderFilterToolTip';
import style from '../DashboardCustomerBob.module.scss';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { getCompany } from 'helpers/getCompanyVariables';

// import HeaderFilterToolTip from 'views/DashboardCustomers/HeaderFilter/HeaderFilterToolTip';
// import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

const excludeNetPremium = ['BCBS OK', 'BCBS TX', 'BCBS IL', 'Aetna'];

const company = getCompany();

const showDateKeys = [
  {
    key: 'callidus_books_of_business_EffectiveDatefromBOB',
    label: 'Effective Date'
  },
  {
    key: 'BOBInactiveDate',
    label: 'Inactive Date'
  },
  {
    key: 'callidus_books_of_business_BOBLastUpdateDate',
    label: 'Last Modified Date'
  }
];

const isLOA = obj => {
  const compLevel =
    obj['Custom_Comp_Level'] !== null
      ? obj['Custom_Comp_Level']
      : obj['Comp_LevelsX'];

  return compLevel === 'LOA';
};

const BobTable = ({
  setCurrentPage,
  currentPage,
  data_for_table,
  bob_records_count,
  setCurrentPageSize,
  setTriggerFetch,
  activePanel,
  setActivePanel,
  formData,
  carriersOptions,
  lastUpdateDate,
  handleChangeSelectValue,
  applyFilters,
  selectedFilterData = null,
  riskManagement
}) => {
  const dispatch = useDispatch();
  const { isDownloadingReport } = useSelector(state => state.commissions);
  const { zohoData } = useSelector(state => state.user);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50
  });

  const notAdminAndLOA = React.useMemo(
    () => zohoData.Position_at_Agency !== USER_TYPES.ADMIN && isLOA(zohoData),
    [zohoData]
  );

  const showCommissionStatementsRules = useMemo(() => {
    return (
      zohoData.Entire_Commissions_Tab === 'Force Show' ||
      (zohoData.Position_at_Agency === USER_TYPES.ADMIN &&
        zohoData.agencyData?.Parent_Account?.id === ONEILL_MAIN_AGENCY_ID &&
        zohoData.Entire_Commissions_Tab !== 'Hide') ||
      (!zohoData.Hide_Commission_Data &&
        zohoData.Entire_Commissions_Tab !== 'Hide' &&
        !zohoData.agencyData?.Hide_Commission_Rates_from_Agents)
    );
  }, [zohoData]);

  const showMoreKeys = [
    {
      key: 'callidus_books_of_business_FFMAppId',
      label: 'FFM App Id'
    },
    {
      key: 'carrierpolicynbr',
      label: 'Policy Number'
    },
    {
      key: 'callidus_books_of_business_EmailAddress',
      label: 'Holder Email'
    },
    {
      key: 'callidus_books_of_business_HolderPhone',
      label: 'Holder Phone'
    },
    {
      key: 'netpremium',
      label: 'Net Premium',
      isNumber: true
    }
  ];

  useEffect(() => {
    if (currentPage - 1 !== pagination.pageIndex) {
      setPagination(current => {
        return {
          ...current,
          pageIndex: currentPage - 1
        };
      });
    }
  }, [currentPage]);

  useEffect(() => {
    if (currentPage - 1 !== pagination.pageIndex) {
      setTriggerFetch(current => current + 1);
      setCurrentPage(pagination.pageIndex + 1);
    }
    setActivePanel({});
  }, [pagination.pageIndex]);

  useEffect(() => {
    setTriggerFetch(current => current + 1);
    setCurrentPageSize(pagination.pageSize);
    setActivePanel({});
  }, [pagination.pageSize]);

  const tableData = useMemo(
    () =>
      data_for_table.map(row => ({
        ...row
      })),
    [data_for_table]
  );

  const columns = useMemo(
    () => [
      {
        header: 'Holder',
        accessorKey: 'callidus_books_of_business_Holder',
        size: 130,
        enableColumnFilter: false,
        flexGrow: 1
      },
      {
        header: 'DOB',
        accessorKey: 'callidus_books_of_business_holderdob',
        size: 130,
        enableColumnFilter: false,
        flexGrow: 1,
        Cell: row => {
          const date = (row.cell.getValue() ?? '//').split('/') ?? '';
          const year = date[2],
            month = date[0],
            dt = date[1];

          return (
            <div>
              {year > 2100 ? 'N/A' : date ? `${month}-${dt}-${year}` : ''}
            </div>
          );
        }
      },
      {
        header: 'State',
        accessorKey: 'callidus_books_of_business_HolderState',
        enableColumnFilter: false,
        size: 80
      },
      {
        header: 'Carrier',
        accessorKey: 'callidus_books_of_business_carriername',
        flexGrow: 1,
        enableColumnFilter: false,
        size: 120
      },
      {
        header: 'NPN',
        accessorKey: 'callidus_books_of_business_WritingAgentNPNFromBOB',
        enableColumnFilter: false,
        size: 100
      },
      {
        header: 'Agent',
        accessorKey: 'callidus_books_of_business_WritingAgentNameFromBOB',
        enableResizing: false,
        Filter: ({}) => {
          const options = ['Active', 'Inactive'];

          return (
            <HeaderFilterToolTip
              forceUpdateProp={selectedFilterData}
              applyFilters={applyFilters}
              initialData={formData.agentStatus}
              clearFilters={() => handleChangeSelectValue([], 'agentStatus')}
              statusName='agent'
            >
              <div
                style={{
                  minWidth: '200px',
                  padding: '16px 16px 0px 16px'
                }}
              >
                <FormGroup>
                  {options.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={formData.agentStatus.includes(option)}
                        />
                      }
                      label={option}
                      onChange={() =>
                        handleChangeSelectValue(
                          formData.agentStatus.includes(option)
                            ? formData.agentStatus.filter(o => o !== option)
                            : [...formData.agentStatus, option],
                          'agentStatus'
                        )
                      }
                    />
                  ))}
                </FormGroup>
              </div>
            </HeaderFilterToolTip>
          );
        },
        flexGrow: 1,
        size: 160
      },
      {
        header: 'Member Status',
        accessorKey: 'callidus_books_of_business_MemberStatus',
        enableResizing: false,
        Filter: ({}) => {
          if (riskManagement) return null;
          const options = ['Active', 'Inactive', 'Pending', 'At Risk'];

          return (
            <HeaderFilterToolTip
              forceUpdateProp={selectedFilterData}
              applyFilters={applyFilters}
              initialData={formData.memberStatus}
              clearFilters={() => handleChangeSelectValue([], 'memberStatus')}
            >
              <div
                style={{
                  minWidth: '200px',
                  padding: '16px 16px 0px 16px'
                }}
              >
                <FormGroup>
                  {options.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={formData.memberStatus.includes(option)}
                        />
                      }
                      label={option}
                      onChange={() =>
                        handleChangeSelectValue(
                          formData.memberStatus.includes(option)
                            ? formData.memberStatus.filter(o => o !== option)
                            : [...formData.memberStatus, option],
                          'memberStatus'
                        )
                      }
                    />
                  ))}
                </FormGroup>
              </div>
            </HeaderFilterToolTip>
          );
        },
        Cell: data => {
          const label =
            data.cell.row.original.callidus_books_of_business_MemberStatus;

          return (
            <span
              style={{
                color:
                  label === 'Active' || label === 'On BOB'
                    ? '#3dcc21'
                    : label === 'Inactive' || label === 'Dropped'
                    ? 'red'
                    : label === 'At Risk'
                    ? 'orange'
                    : ''
              }}
            >
              {label}
            </span>
          );
        },
        flexGrow: 1,
        size: 185
      },
      {
        header: 'BOB Status',
        accessorKey: 'fallenOff',
        size: 100,
        flexGrow: 1,
        enableResizing: false,
        ...(company === 'oneill'
          ? { Filter: () => <></> }
          : {
              Filter: ({}) => {
                const options = ['Dropped', 'On BOB'];

                return (
                  <HeaderFilterToolTip
                    forceUpdateProp={selectedFilterData}
                    applyFilters={applyFilters}
                    initialData={formData.fallenOff}
                    clearFilters={() =>
                      handleChangeSelectValue([], 'fallenOff')
                    }
                  >
                    <div
                      style={{
                        minWidth: '200px',
                        padding: '16px 16px 0px 16px'
                      }}
                    >
                      <FormGroup>
                        {options.map(option => (
                          <FormControlLabel
                            key={option}
                            control={
                              <Checkbox
                                checked={formData.fallenOff.includes(option)}
                              />
                            }
                            label={option}
                            onChange={() =>
                              handleChangeSelectValue(
                                formData.fallenOff.includes(option)
                                  ? formData.fallenOff.filter(o => o !== option)
                                  : [...formData.fallenOff, option],
                                'fallenOff'
                              )
                            }
                          />
                        ))}
                      </FormGroup>
                    </div>
                  </HeaderFilterToolTip>
                );
              }
            }),
        Cell: data => {
          const currentDate = new Date();

          const removedDate = new Date(data.cell.row.original.BOBRemovedDate);
          const dropped =
            data.cell.row.original.BOBExlude === 'Y' ||
            (data.cell.row.original.BOBRemovedDate &&
              removedDate <= currentDate);

          return (
            <div>
              <div
                style={{
                  color: dropped ? 'red' : '#3dcc21'
                }}
              >
                {dropped ? 'Dropped' : 'On BOB'}
              </div>
              {data.cell.row.original.BOBRemovedDate && (
                <div>
                  <span
                    style={{
                      fontStyle: 'italic',
                      fontSize: '12px'
                    }}
                  >
                    {(removedDate.getMonth() + 1).toString().padStart(2, '0')}-
                    {removedDate.getDay().toString().padStart(2, '0')}-
                    {removedDate.getFullYear()}
                  </span>
                </div>
              )}
            </div>
          );
        }
      },
      {
        header: 'Paid to Date',
        accessorKey: 'callidus_books_of_business_PaidToDate',
        enableColumnFilter: false,
        enableResizing: false,
        size: 120,
        Cell: data => {
          const dateStr = data.cell.getValue();
          return dateStr ? (
            <div>{dateStr.replace(/\//g, '-')}</div>
          ) : (
            <div>-</div>
          );
        }
      },
      ...(!notAdminAndLOA && showCommissionStatementsRules
        ? [
            {
              header: 'Last Commission Statement',
              accessorKey: 'lastCommissionStatementDate',
              enableColumnFilter: false,
              enableResizing: false,
              size: 215,
              Cell: data => {
                const dateStr = data.cell.getValue();
                return dateStr ? (
                  <div>{dateStr.replace(/\//g, '-')}</div>
                ) : (
                  <div>-</div>
                );
              }
            }
          ]
        : [])
    ],
    [formData, selectedFilterData]
  );

  const handleDownloadRows = useCallback(() => {
    const carriers =
      formData.carriers && formData.carriers.length > 0
        ? formData.carriers
        : carriersOptions;

    dispatch(
      downloadAllBOBData({
        ...formData,
        carriers: carriers.map(carrier => carrier.Callidus_Carrier_Names).flat()
      })
    );
  }, [formData]);

  const table = useMaterialReactTable({
    columns,
    data: [...tableData],
    enableColumnActions: false,
    enableColumnResizing: true,
    enableTopToolbar: false,
    enableSorting: false,
    enableRowSelection: false,
    enableStickyHeader: true,
    initialState: { showColumnFilters: true },
    paginationDisplayMode: 'pages',
    rowCount: Number(bob_records_count),
    manualPagination: true,
    onPaginationChange: setPagination,
    onExpandedChange: active => {
      setActivePanel(active);
    },
    state: {
      pagination: pagination,
      expanded: activePanel
    },
    muiPaginationProps: {
      rowsPerPageOptions: [50, 100],
      showFirstButton: false,
      showLastButton: false,
      variant: 'outlined',
      style: {
        paddingRight: '60px'
      }
    },
    muiTablePaperProps: {
      elevation: 0, //change the mui box shadow
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 0
      },
      className: riskManagement
        ? lastUpdateDate
          ? style.tableHeightRiskManagementAdjusted
          : style.tableHeightRiskManagement
        : lastUpdateDate
        ? style.tableHeightAdjusted
        : style.tableHeight
    },
    muiTableContainerProps: {
      id: 'customers-bob-table-body',
      style: {
        flex: 1
      }
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: ''
      }
    },
    renderBottomToolbarCustomActions: ({}) => (
      <Button
        isDisabled={isDownloadingReport}
        text='Export All'
        variant='secondary'
        onClick={handleDownloadRows}
        isLoading={isDownloadingReport}
      />
    ),
    muiTableHeadCellProps: {
      style: {
        backgroundColor: '#FAFAFA',
        padding: '10px 5px',
        justifyContent: 'center'
      }
    },
    muiTableBodyCellProps: {
      style: {
        padding: '10px 5px'
      }
    },
    manualPagination: true,
    renderEmptyRowsFallback: () => (
      <h2
        style={{
          color: 'grey',
          textAlign: 'center',
          fontWeight: '400',
          fontSize: 14,
          padding: 15
        }}
      >
        No BOB records
      </h2>
    ),
    muiExpandButtonProps: ({ row, table }) => ({
      onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }), //only 1 detail panel open at a time
      sx: {
        transform: row.getIsExpanded() ? 'rotate(0deg)' : 'rotate(-0deg)',
        transition: 'transform 0.2s'
      },
      disabled: false
    }),
    enableExpanding: true,
    enableExpandAll: false,
    muiTableBodyCellProps: ({ column }) => {
      const { columnDef } = column;
      if (columnDef.id === 'mrt-row-spacer')
        return {
          style: {
            maxWidth: '0px'
          }
        };
      return {
        className: style[`react-table-column-flex-grow-${columnDef.flexGrow}`]
      };
    },
    muiTableHeadCellProps: ({ column }) => {
      const { columnDef } = column;

      const hasFilters = columnDef.Filter;

      if (columnDef.id === 'mrt-row-spacer')
        return {
          style: {
            maxWidth: '0px'
          }
        };
      return {
        className: `${
          style[`react-table-column-flex-grow-${columnDef.flexGrow}`]
        } ${hasFilters ? style['react-table-head-content'] : ''}`,
        style: {
          display: 'flex',
          flexDirection: 'row',
          gap: '10px'
        }
      };
    },
    renderDetailPanel: ({ row }) => {
      const carrier = row.original.callidus_books_of_business_carriername;

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '25px'
          }}
        >
          {showMoreKeys.map(({ key, label, style, isNumber }) => {
            const showDash =
              excludeNetPremium.includes(carrier) && key === 'netpremium';
            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  ...style
                }}
              >
                <label
                  style={{
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {label}
                </label>
                <p>
                  {showDash
                    ? '-'
                    : isNumber
                    ? `$${Number(row?.original[key]).toFixed(2)}`
                    : `${row?.original[key]}` || '-'}
                </p>
              </div>
            );
          })}
          {showDateKeys.map(({ key, label }) => {
            const date = row?.original[key]?.split('/') ?? '';
            const year = date[2],
              month = date[0],
              dt = date[1];

            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <label
                  style={{
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {label}
                </label>
                <p>
                  {year > 2100 ? 'N/A' : date ? `${month}-${dt}-${year}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      );
    },
    muiDetailPanelProps: () => ({
      sx: () => ({
        backgroundColor: 'rgba(227,246,255,1)'
      })
    })
  });

  return (
    <div
      style={{
        width: '100%',
        flex: 1,
        display: 'flex'
      }}
    >
      <MaterialReactTable table={table} />
    </div>
  );
};

BobTable.propTypes = {
  setCurrentPage: PropTypes.func,
  currentPage: PropTypes.number,
  data_for_table: PropTypes.array,
  bob_records_count: PropTypes.number,
  setCurrentPageSize: PropTypes.func,
  setTriggerFetch: PropTypes.func,
  setActivePanel: PropTypes.func,
  activePanel: PropTypes.object,
  formData: PropTypes.object,
  carriersOptions: PropTypes.array,
  handleChangeSelectValue: PropTypes.func,
  applyFilters: PropTypes.func,
  selectedFilterData: PropTypes.any,
  lastUpdateDate: PropTypes.string,
  riskManagement: PropTypes.bool
};

export default memo(BobTable);

const CircleIcon = icons['circle'];
const WarningIcon = icons['warning'];

const CustomCells = ({ Value, Column }) => {
  const colorIconConfig = {
    StatusfromBOB: {
      NotAppointed: {
        color: '#E85C53',
        icon: <WarningIcon fontSize='small' />,
        text: 'Not Appointed'
      },
      Active: {
        color: '#5DBB29',
        icon: <></>,
        text: 'Active'
      },
      Ignore: {
        color: '#909090',
        icon: <WarningIcon fontSize='small' />,
        text: 'Ignore'
      },
      InProgress: {
        color: '#E0DA1F',
        icon: <WarningIcon fontSize='small' />,
        text: 'In Progress'
      }
    },
    BobStatus: {
      Matched: {
        color: '#5DBB29',
        icon: <CircleIcon fontSize='small' />,
        text: 'Matched'
      },
      ManuallyMatched: {
        color: '#008000',
        icon: <CircleIcon fontSize='small' />,
        text: 'Manually Matched'
      },
      ManuallyUnmatched: {
        color: '#C70039',
        icon: <WarningIcon fontSize='small' />,
        text: 'Manually Unmatched'
      },
      TwoWeeksPast: {
        color: '#E85C53',
        icon: <WarningIcon fontSize='small' />,
        text: '>2 Weeks Past'
      },
      PendingMatch: {
        color: '#E4D00A',
        icon: <WarningIcon fontSize='small' />,
        text: 'Pending Match'
      },
      AssignedToOther: {
        color: '#F09F0C',
        icon: <WarningIcon fontSize='small' />,
        text: 'Check AOR'
      },
      Ignore: {
        color: '#909090',
        icon: <WarningIcon fontSize='small' />,
        text: 'Ignore'
      },
      EmailedCarrier: {
        color: '#000000',
        icon: <WarningIcon fontSize='small' />,
        text: 'Emailed Carrier'
      }
    }
  };

  return (
    <div
      style={{
        color: colorIconConfig[Column][Value]?.color ?? '',
        height: '24px',
        width: '100%',
        padding: '0',
        margin: '0',
        lineHeight: '1',
        borderRadius: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}
    >
      {colorIconConfig[Column][Value]?.icon}
      {colorIconConfig[Column][Value]?.text ?? 'N/A'}
    </div>
  );
};

CustomCells.propTypes = {
  Value: PropTypes.string,
  Column: PropTypes.string
};
